import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { BRAND } from '../../brand';
import { fetchBillingCatalog, requestMagicLink, openBillingPortal } from '../../services/api';

/**
 * Account gate: magic-link sign-in + Stripe Payment Link checkout.
 * Dollar amounts come only from GET /api/billing/catalog (config/pricing.js).
 */
const AuthenticationGate = ({ open, reason, onClose }) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    fetchBillingCatalog()
      .then(setCatalog)
      .catch(() => setCatalog(null));
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const formatUsd = (n) =>
    typeof n === 'number'
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)
      : null;

  const onMagicLink = async (e) => {
    e?.preventDefault();
    if (!email.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSending(true);
    try {
      await requestMagicLink(email.trim());
      toast.success('Check your email for a sign-in link');
    } catch (err) {
      toast.error(err.message || 'Could not send sign-in link');
    } finally {
      setSending(false);
    }
  };

  const openLink = (url, label) => {
    if (!url) {
      toast.error(`${label} checkout is not configured yet`);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="mk-modal-root" role="dialog" aria-modal="true" aria-labelledby="auth-gate-title">
      <button type="button" className="mk-modal-backdrop" aria-label="Close" onClick={onClose} />
      <div className="mk-modal">
        <button type="button" className="mk-modal-close" onClick={onClose} aria-label="Close dialog">
          <X size={18} />
        </button>
        <p className="mk-eyebrow">Account</p>
        <h2 id="auth-gate-title" className="mk-modal-title">
          Continue with {BRAND.name}
        </h2>
        <p className="mk-modal-body">
          {reason ||
            'Sign in with email to use credits, or buy a Single Opinion / Desk subscription. Your first Opinion is free with email only — no card.'}
        </p>

        <form className="mk-modal-actions" onSubmit={onMagicLink} style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.75rem' }}>
          <label className="mk-sr-only" htmlFor="auth-email">
            Email
          </label>
          <input
            id="auth-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mk-search-input"
          />
          <button type="submit" className="mk-btn mk-btn-primary" disabled={sending}>
            {sending ? 'Sending…' : 'Email me a sign-in link'}
          </button>
        </form>

        <div className="mk-modal-actions" style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="mk-btn mk-btn-ghost"
            onClick={() => openLink(catalog?.singleOpinion?.paymentLinkUrl, 'Single Opinion')}
          >
            Buy Single Opinion
            {catalog?.singleOpinion?.amountUsd != null
              ? ` (${formatUsd(catalog.singleOpinion.amountUsd)})`
              : ''}
          </button>
          <button
            type="button"
            className="mk-btn mk-btn-ghost"
            onClick={() => openLink(catalog?.desk?.paymentLinkUrl, 'Desk')}
          >
            Subscribe to Desk
            {catalog?.desk?.amountUsd != null ? ` (${formatUsd(catalog.desk.amountUsd)}/mo)` : ''}
          </button>
        </div>

        <div className="mk-modal-actions" style={{ marginTop: '0.75rem' }}>
          <button
            type="button"
            className="mk-btn mk-btn-ghost"
            onClick={async () => {
              try {
                const { url } = await openBillingPortal();
                window.location.href = url;
              } catch (err) {
                toast.error(err.message || 'Sign in first, then manage billing');
              }
            }}
          >
            Manage billing
          </button>
        </div>

        <p className="mk-modal-note">
          No passwords. Declined Opinions are never charged.
        </p>
      </div>
    </div>
  );
};

export default AuthenticationGate;
